<template>
  <form class="contents" novalidate @submit.prevent="save">
    <tabs />
    <toolbar>
      <action :icon="ResetIcon" :label="$t('pages.modal.resetPassword.buttonText')" @click="passwordResetModalOpen = true" />
      <action :icon="ResetIcon" :label="$t('pages.modal.resendConfirmation.buttonText')" @click="resendConfirmationModalOpen = true" />
      <action :icon="TrashIcon" :label="$t('pages.driver.details.delete.buttonText')" @click="deleteDriver" />
    </toolbar>
    <delete-box-modal v-model:open="boxModalOpen" v-model:box="driver.box" :driver="driver" @saved="emit('reload')" />
    <password-reset-modal v-model:open="passwordResetModalOpen" :user="modelValue?.user" />
    <resend-confirmation-modal v-model:open="resendConfirmationModalOpen" :user="modelValue?.user" />
    <menu-bar class="flex justify-center" :hidden="!isDirty">
      <div class="w-card-content flex justify-end gap-x-7.5">
        <custom-button variant="outlined" @click="reset">
          {{ $t('reset') }}
        </custom-button>
        <custom-button variant="primary" type="submit" :busy="loading">
          {{ $t('save') }}
        </custom-button>
      </div>
    </menu-bar>
    <cards class="mb-20">
      <card :title="$t('pages.driver.details.sections.personal.title')">
        <section class="card-section">
          <Multiselect
            v-model="driver.user.salutation"
            :error="errors?.user?.salutation"
            class="w-fit"
            :label="$t('user.salutation')"
            :placeholder="$t('user.salutation')"
            :options="
              enumOptionsProvider('/enums/salutation', (v: string) => ({
                label: t(`title.${v}`),
                value: v,
              }))
            "
            :can-deselect="false"
            :can-clear="false"
          />
          <FormInput
            v-model="driver.user.firstName"
            :error="errors?.user?.firstName"
            class="col-start-1 col-end-2"
            type="text"
            :label="$t('user.firstName')"
            :placeholder="$t('user.firstName')"
          />
          <FormInput
            v-model="driver.user.lastName"
            :error="errors?.user?.lastName"
            class="flex-1"
            type="text"
            :label="$t('user.lastName')"
            :placeholder="$t('user.lastName')"
          />
          <FormInput
            v-model="driver.user.email"
            :error="errors?.user?.email"
            class="col-start-1"
            type="email"
            :label="$t('user.email')"
            placeholder="name@mail.com"
          />
          <FormInput
            v-model="driver.user.phoneNumber"
            :error="errors?.user?.phoneNumber"
            class="col-start-1"
            type="tel"
            :label="$t('user.phoneNumber')"
            :placeholder="$t('user.callNumber')"
          />
          <Multiselect
            v-model="driver.region"
            :error="errors?.region"
            class="col-start-1"
            :label="$t('region')"
            :options="fetchRegions"
            :can-deselect="false"
            :can-clear="false"
            :searchable="true"
          />
          <Multiselect
            v-model="driver.user.language"
            :error="errors?.user?.language"
            :label="$t('user.language')"
            :options="fetchLanguages"
            :can-deselect="false"
            :can-clear="false"
            :searchable="true"
          />
          <DatePicker
            v-model="driver.user.birthday"
            :error="errors?.user?.birthday"
            class="col-start-1"
            type="birthday"
            :label="$t('user.birthday')"
          />
          <FormInput
            v-model="driver.user.address.street"
            :error="errors?.user?.address?.street"
            class="col-start-1"
            type="text"
            :label="`${$t('address.street')} / ${$t('address.houseNumber')}`"
            :placeholder="$t('address.streetAndNumber')"
          />
          <div class="col-start-1 flex gap-x-5">
            <FormInput
              v-model="driver.user.address.zip"
              :error="errors?.user?.address?.zip"
              class="flex-1"
              type="text"
              :label="$t('address.zip')"
              :placeholder="$t('address.zip')"
            />
            <FormInput
              v-model="driver.user.address.cityName"
              :error="errors?.user?.address?.cityName"
              class="flex-auto"
              type="text"
              :label="$t('address.city')"
              :placeholder="$t('address.location')"
            />
          </div>
        </section>
      </card>
      <card :title="$t('pages.driver.details.sections.care.title')">
        <section class="card-section">
          <DatePicker v-model="driver.caredForAt" :error="errors?.caredForAt" class="col-start-1" mode="datetime" :label="$t('driver.caredForAt')" />
          <Multiselect
            v-model="driver.noticeComment"
            :error="errors?.noticeComment"
            :options="fetchDriverNoticeComment"
            :label="$t('driver.noticeComment.DEFAULT')"
            :placeholder="$t('driver.noticeComment.null')"
            :can-deselect="true"
            :can-clear="true"
            :searchable="true"
            class="w-full"
          />
        </section>
      </card>
      <card :title="$t('pages.driver.details.sections.lastCampaigns.title')">
        <section class="card-section">
          <DatePicker :model-value="driver.endDateLastFinishedCampaign" mode="date" :label="$t('driver.endDateLastFinishedCampaign')" disabled />
          <DatePicker :model-value="driver.endDateLastRegisteredCampaign" mode="date" :label="$t('driver.endDateLastRegisteredCampaign')" disabled />
        </section>
      </card>
      <card :title="$t('pages.driver.details.sections.box.title')">
        <template v-if="isBoxEmpty" #actions>
          <CustomButton variant="text" @click.prevent="addBox">
            {{ $t('pages.driver.details.sections.box.addBoxButton') }}
            <AddIcon class="add-icon" />
          </CustomButton>
        </template>
        <template v-else #actions>
          <CustomButton variant="text" @click.prevent="removeBox">
            {{ $t('pages.driver.details.sections.box.removeBoxButton') }}
            <TrashIcon class="trash" />
          </CustomButton>
        </template>
        <template v-if="!isBoxEmpty && driver.box" #default>
          <section v-if="driver.box" class="card-section">
            <FormInput v-model="driver.box.sealNumber" :error="errors?.box?.sealNumber" type="text" :label="$t('box.seal')" />
            <Multiselect
              v-model="driver.box.status"
              :error="errors?.box?.status"
              :options="
                enumOptionsProvider('/enums/BoxStatus', (item) => ({
                  label: $t(`box.status.${item}`),
                  value: item,
                }))
              "
              :label="$t('box.statusTitle')"
              :can-deselect="false"
              :can-clear="false"
            />
            <Multiselect
              v-model="driver.box.type"
              :error="errors?.box?.type"
              :options="
                enumOptionsProvider('/enums/BoxType', (item) => ({
                  label: $t(`box.types.${item}`),
                  value: item,
                }))
              "
              :label="$t('box.type')"
              :can-deselect="false"
              :can-clear="false"
            />
            <Multiselect
              v-model="driver.box.variant"
              :error="errors?.box?.variant"
              :options="
                enumOptionsProvider('/enums/BoxVariant', (item) => ({
                  label: $t(`box.variant.${item}`),
                  value: item,
                }))
              "
              :label="$t('box.variant.DEFAULT')"
              :can-deselect="false"
              :can-clear="false"
            />
            <DatePicker v-model="driver.box.mountingDate" :error="errors?.box?.mountingDate" mode="datetime" :label="$t('box.mountingDate')" />
            <Multiselect
              v-model="driver.box.firstCampaign"
              :error="errors?.box?.firstCampaign"
              :options="campaignList"
              :label="$t('box.campaign')"
              :can-deselect="false"
              :can-clear="false"
              :searchable="true"
              :minChars="2"
              :filterResults="false"
              :delay="500"
              :resolveOnLoad="true"
              :createOption="false"
              :clearOnSelect="true"
              :noOptionsText="$t('inputSearchString')"
            />
            <FormInput v-model="driver.box.keyNumber" :error="errors?.box?.keyNumber" type="text" :label="$t('box.keyNumber')" />
            <Multiselect
              v-model="driver.box.stable"
              :error="errors?.box?.stable"
              :label="$t('box.stable')"
              :options="[
                {
                  value: 'true',
                  label: $t('yes'),
                },
                {
                  value: 'false',
                  label: $t('no'),
                },
              ]"
              :can-deselect="false"
              :can-clear="false"
            />
            <FormInput v-model="driver.box.alreadyPayed" :error="errors?.box?.alreadyPayed" type="number" :label="$t('box.alreadyPayed')" />
            <FormInput v-model="driver.box.totalPrice" :error="errors?.box?.totalPrice" type="number" :label="$t('box.totalPrice')" disabled />
            <FormInput v-model="driver.box.depot" :error="errors?.box?.depot" type="number" :label="$t('box.depot')" disabled />
            <Multiselect
              v-model="driver.box.boxBaseSurface"
              :error="errors?.box?.boxBaseSurface"
              :label="$t('box.boxBaseSurface.DEFAULT')"
              :options="boxBaseSurfaceOptions"
              :can-deselect="false"
              :can-clear="false"
            />
          </section>
        </template>
      </card>
      <card :title="$t('pages.driver.details.sections.bank.title')">
        <section class="card-section">
          <FormInput v-model="driver.iban" :error="errors?.iban" type="text" :label="$t('driver.iban')" placeholder="CH25 0XXX XXXX XXXX XXX X" />
          <FormInput
            v-model="driver.bankName"
            :error="errors?.bankName"
            type="text"
            :label="$t('driver.bankName')"
            :placeholder="$t('driver.bankName')"
          />
          <FormInput v-model="driver.payee" :error="errors?.payee" type="text" :label="$t('driver.payee')" :placeholder="$t('driver.payee')" />
        </section>
      </card>
      <card :title="$t('pages.driver.details.sections.profile.title')">
        <section class="card-section">
          <div class="flex gap-x-5 col-start-1 col-end-3">
            <Switch
              v-model="driver.user.active"
              :error="errors?.user?.active"
              :label="$t('user.active')"
              :options="{ yes: $t('user.isActive.true'), no: $t('user.isActive.false') }"
            />
            <Switch v-model="driver.profileComplete" :error="errors?.profileComplete" :label="$t('driver.profileComplete')" />
            <Switch
              v-model="driver.liabilityDisclaimerSigned"
              :error="errors?.liabilityDisclaimerSigned"
              :label="$t('driver.liabilityDisclaimerSigned')"
            />
            <Switch v-model="driver.verificationRequested" :error="errors?.verificationRequested" :label="$t('driver.verificationRequested')" />
          </div>
          <FormInput
            :model-value="driver.user.emailVerifiedOn ? format(new Date(driver.user.emailVerifiedOn), 'dd.MM.yyyy') : '-'"
            type="text"
            :label="$t('user.emailVerifiedOn')"
            disabled
          />
          <div class="flex gap-x-5 col-start-1 col-end-3">
            <FormInput
              :value="t(`driver.driverType.${driver.driverTypeAtRegistration}`)"
              type="text"
              :label="$t('driver.driverTypeAtRegistration')"
              disabled
            />
            <FormInput
              :value="t(`driver.driverType.${driver.driverType}`)"
              type="text"
              :label="$t('driver.driverType.DEFAULT')"
              disabled
            />
          </div>
          <div class="flex gap-x-5 col-start-1 col-end-3">
            <Multiselect
              v-model="driver.warningLevel"
              :error="errors?.warningLevel"
              :options="enumOptionsProvider('/enums/WarningLevel', (item: string) => ({
                  value: item,
                  label: $t(`driver.warningLevel.${item}`),
                }))"
              :label="$t('driver.warningLevel.DEFAULT')"
              :can-deselect="false"
              :can-clear="false"
              class="w-1/2"
            />
          </div>
          <div class="flex items-end gap-x-5 w-full">
            <Switch v-model="driver.specialDriverPhoto" :error="errors?.specialDriverPhoto" :label="$t('driver.specialPhoto')" />
            <Switch v-model="driver.specialDriverStanding" :error="errors?.specialDriverStanding" :label="$t('driver.specialStanding')" />
            <Switch v-model="driver.helperDriver" :error="errors?.helperDriver" :label="$t('driver.helper')" />
          </div>
          <Multiselect
            v-model="driver.bicycleType"
            :error="errors?.bicycleType"
            class="col-start-1"
            :options="
              enumOptionsProvider('/enums/Bicycle', (item) => ({
                value: item,
                label: $t('driver.bicycleTypes.' + item),
              }))
            "
            :label="$t('driver.bicycleType')"
            :can-deselect="false"
            :can-clear="false"
            :searchable="true"
          />
          <FormInput v-model="driver.outstandingDepot" :error="errors?.outstandingDepot" type="number" :label="$t('driver.outstandingDepot')" />
          <div class="form-group col-start-1 col-end-3">
            <Field :label="$t('driver.mostFrequentPathStart')" :field-attrs="{ class: '!grid grid-cols-2 gap-x-5' }" unstyled>
              <FormInput
                v-model="driver.defaultSourceAddress.street"
                :error="errors?.defaultSourceAddress?.street"
                type="text"
                :placeholder="$t('address.streetAndNumber')"
              />
              <div class="flex gap-x-5">
                <FormInput
                  v-model="driver.defaultSourceAddress.zip"
                  :error="errors?.defaultSourceAddress?.zip"
                  type="text"
                  class="w-fit"
                  :placeholder="$t('address.zip')"
                />
                <FormInput
                  v-model="driver.defaultSourceAddress.cityName"
                  :error="errors?.defaultSourceAddress?.cityName"
                  type="text"
                  class="w-fit"
                  :placeholder="$t('address.city')"
                />
              </div>
            </Field>
            <Field :label="$t('driver.mostFrequentPathEnd')" :field-attrs="{ class: '!grid grid-cols-2 gap-x-5' }" unstyled>
              <FormInput
                v-model="driver.defaultDestinationAddress.street"
                :error="errors?.defaultDestinationAddress?.street"
                type="text"
                :placeholder="$t('address.streetAndNumber')"
              />
              <div class="flex gap-x-5">
                <FormInput
                  v-model="driver.defaultDestinationAddress.zip"
                  :error="errors?.defaultDestinationAddress?.zip"
                  type="text"
                  :placeholder="$t('address.zip')"
                />
                <FormInput
                  v-model="driver.defaultDestinationAddress.cityName"
                  :error="errors?.defaultDestinationAddress?.cityName"
                  type="text"
                  :placeholder="$t('address.city')"
                />
              </div>
            </Field>
            <div class="flex gap-x-5">
              <FormInput
                v-model="driver.estToursPerWeek"
                :error="errors?.estToursPerWeek"
                type="number"
                step="any"
                class="w-1/2"
                :label="$t('driver.ridePerWeek')"
              />
              <FormInput
                v-model="driver.estAvgKmPerDay"
                :error="errors?.estAvgKmPerDay"
                type="number"
                step="any"
                class="w-1/2"
                :label="$t('driver.avgKmPerWeek')"
              />
            </div>
          </div>
          <FormInput
            :model-value="driver.acceptedAgbVersions?.registration ?? 0"
            type="number"
            :label="$t('driver.acceptedAgbVersions.registration')"
            disabled
          />
          <FormInput
            :model-value="driver.acceptedAgbVersions?.privacy ?? 0"
            type="number"
            :label="$t('driver.acceptedAgbVersions.privacy')"
            disabled
          />
        </section>
      </card>
      <card :title="$t('pages.driver.details.sections.statistics.title')">
        <section class="card-section">
          <FormInput :model-value="driver.totalPoints" type="number" :label="$t('driver.totalPoints')" disabled />
          <FormInput :model-value="driver.pointsLastWeek" type="number" :label="$t('driver.pointsLastWeek')" disabled />
          <FormInput :model-value="driver.rankLastWeek" type="number" :label="$t('driver.currentRank')" disabled />
          <FormInput :model-value="driver.rankStatus" :label="$t('driver.statusEarned')" disabled />
          <Switch
            v-model="driver.requestedLoyaltyPointReward"
            :error="errors?.requestedLoyaltyPointReward"
            :label="$t('driver.requestedLoyaltyPointReward')"
          />
        </section>
      </card>
      <card :title="$t('pages.driver.details.sections.forBusiness.title')">
        <section class="card-section">
          <div class="flex gap-x-5 col-start-1 col-end-3">
            <Switch v-model="driver.companyProfileComplete" :error="errors?.companyProfileComplete" :label="$t('driver.companyProfileComplete')" />
            <Switch v-model="driver.anonymousRanking" :nullable="true" :error="errors?.anonymousRanking" :label="$t('driver.anonymousRanking')" />
          </div>
          <Multiselect
            v-model="driver.canton"
            :error="errors?.canton"
            :label="$t('driver.canton')"
            :placeholder="$t('driver.canton')"
            :options="
              enumOptionsProvider('/enums/Canton', (v: string) => ({
                label: t(`canton.${v}`),
                value: v,
              }))
            "
          />
          <Multiselect
            v-model="driver.batteryPosition"
            :error="errors?.batteryPosition"
            :label="$t('driver.batteryPosition.DEFAULT')"
            :placeholder="$t('driver.batteryPosition.DEFAULT')"
            :options="
              enumOptionsProvider('/enums/BatteryPosition', (v: string) => ({
                label: t(`driver.batteryPosition.${v}`),
                value: v,
              }))
            "
          />
          <div class="flex gap-x-5 col-start-1 col-end-3">
            <Switch v-model="driver.luggageRackUsable" :nullable="true" :error="errors?.luggageRackUsable" :label="$t('driver.luggageRackUsable')" />
            <Switch v-model="driver.luggageRackInBack" :nullable="true" :error="errors?.luggageRackInBack" :label="$t('driver.luggageRackInBack')" />
            <Switch v-model="driver.saddlebags" :nullable="true" :error="errors?.saddlebags" :label="$t('driver.saddlebags')" />
          </div>
        </section>
        <section class="card-section mt-5" v-if="!licenseSeat?.license?.company?.name">
          <p>{{ $t('pages.driver.details.sections.forBusiness.noLicense') }}</p>
        </section>
        <section class="card-section mt-5" v-else>
          <FormInput
            v-model="licenseSeat.license.company.name"
            disabled
            type="text"
            class="col-span-2"
            :label="$t('driver.license')"
            :placeholder="$t('driver.license')"
          />
          <FormInput
            v-model="licenseTypeText"
            disabled
            type="text"
            class="col-span-2"
            :label="$t('driver.licenseType')"
            :placeholder="$t('driver.licenseType')"
          />
          <DatePicker :model-value="licenseSeat.license.startDate" mode="date" :label="$t('licenses.start')" disabled />
          <DatePicker :model-value="licenseSeat.license.endDate" mode="date" :label="$t('licenses.end')" disabled />
          <custom-button variant="primary" @click="removeFromLicenseSeat">
            {{ $t('pages.driver.details.sections.forBusiness.removeFromLicenseSeat') }}
          </custom-button>
        </section>
      </card>
    </cards>
  </form>
</template>

<script lang="tsx" setup>
import TrashIcon from '@images/trash.svg?component';
import AddIcon from '@images/panes/add.svg?component';
import Toolbar from '@components/utils/toolbar/Toolbar.vue';
import Action from '@components/utils/toolbar/Action.vue';
import Cards from '@components/utils/Cards.vue';
import Card from '@components/utils/Card.vue';
import { computed, ref, toRef } from 'vue';
import { useI18n } from 'vue-i18n';
import Multiselect from '@components/utils/Multiselect.vue';
import FormInput from '@components/utils/inputs/Input.vue';
import DatePicker from '@components/utils/inputs/DatePicker.vue';
import Switch from '@components/utils/inputs/Switch.vue';
import Field from '@components/utils/inputs/Field.vue';
import ResetIcon from '@images/reset.svg?component';
import Tabs from '@/components/structure/navigation/Tabs.vue';
import MenuBar from '@/components/utils/MenuBar.vue';
import PasswordResetModal from '@pages/modals/PasswordResetModal.vue';
import ResendConfirmationModal from '@pages/modals/ResendConfirmationModal.vue';
import CustomButton from '@utils/CustomButton.vue';
import DeleteBoxModal from '@pages/modals/DeleteBoxModal.vue';
import type { Box, Campaign, Driver, LicenseSeatRegistration, Region, User } from '@/entities/Entities';
import { FetchError, useFetchAction } from '@/hooks/fetch';
import { useNotify } from '@/components/utils/notifications';
import { useDirty, useValidation } from '@/hooks/form';
import { syncRef } from '@/hooks/state';
import { collOptionsProvider, enumOptionsProvider, ProviderParams } from '@/components/utils/table/hooks/provider';
import type { ByReference, RequireOnly, UpdateKeys } from '@/utils/types.js';
import TextArea from '@utils/inputs/TextArea.vue';
import { format } from 'date-fns';
import { boolean, object, string, date, number, mixed, lazy } from 'yup';
import { useDelete } from '@hooks/api';
import { useDeletePrompt } from '@utils/prompt/injects';
import { LicenseType } from '@/enums/Enums';

const DRIVER_DELETE_BOX_ERRORS = ['driver_has_box', 'driver_has_license_seat'];

interface Props {
  modelValue: Driver;
}

const props = defineProps<Props>();

const { t } = useI18n();

const passwordResetModalOpen = ref(false);
const resendConfirmationModalOpen = ref(false);
const boxModalOpen = ref(false);

const [exec, { loading }] = useFetchAction();
const emit = defineEmits(['update:modelValue', 'reload']);
const model = toRef(props, 'modelValue');

const promptDelete = useDeletePrompt();
const deleteDriver = async () => {
  try {
    const answer = await promptDelete?.();
    if (!answer) return answer ?? false;

    await exec(`/users/${props.modelValue?.user?.id}`, {
      method: 'DELETE',
    });

    notify?.({
      variant: 'success',
      title: t('deleteSuccess'),
    });
    emit('reload');
  } catch (e) {
    if (e instanceof FetchError) {
      const errorCode = await e.response.json();
      if (DRIVER_DELETE_BOX_ERRORS.includes(errorCode?.error?.key)) {
        notify?.({
          variant: 'error',
          title: t('pages.driver.details.delete.errorMessage.' + errorCode?.error?.key),
        });
      } else {
        notify?.({
          variant: 'error',
          title: t('deleteError'),
        });
      }
    } else {
      notify?.({
        variant: 'error',
        title: t('deleteError'),
      });
    }
  }
};

const getDriverCopy = (currentDriver: Driver) => ({
  ...currentDriver,
  region: currentDriver?.region?.['@id'],
  defaultSourceAddress: {
    ...(currentDriver?.defaultSourceAddress ?? {}),
  },
  defaultDestinationAddress: {
    ...(currentDriver?.defaultDestinationAddress ?? {}),
  },
  caredForAt: currentDriver?.caredForAt ? new Date(currentDriver.caredForAt) : null,
  user: {
    ...currentDriver?.user,
    address: {
      ...(currentDriver?.user.address ?? {}),
    },
    birthday: currentDriver?.user.birthday ? new Date(currentDriver?.user.birthday) : undefined,
  },
  box: (currentDriver?.box
    ? {
        ...currentDriver?.box,
        sealNumber: currentDriver?.box?.sealNumber ?? '',
        firstCampaign: currentDriver?.box?.firstCampaign?.['@id'],
        mountingDate: currentDriver?.box?.mountingDate ? new Date(currentDriver?.box?.mountingDate) : new Date(),
      }
    : undefined) as ByReference<Box, 'firstCampaign'> | undefined,
  licenseSeat: currentDriver?.licenseSeat ?? { license: { company: { name: '' }, startDate: '', endDate: '' }, type: '' },
  licenseSeatRegistrations: (currentDriver?.licenseSeatRegistrations
    ? [...currentDriver?.licenseSeatRegistrations.map((registration) => ({ ...registration }))]
    : []) as LicenseSeatRegistration[],
});

const dirtyRef = syncRef(model, getDriverCopy);
const driver = syncRef(model, getDriverCopy);

const isBoxEmpty = computed(() => !driver.value.box);

const isDirty = useDirty(driver, dirtyRef);

const findLatestLicenseSeat = (licenseSeatRegistrations: LicenseSeatRegistration[]) => {
  return licenseSeatRegistrations.find((licenseSeatRegistration) => licenseSeatRegistration.endDate === null);
};

const licenseSeat = computed(() => {
  // Only show licenseSeat if there's an active registration (endDate === null)
  // Check driver.value instead of model.value to get real-time updates
  const hasActiveRegistration = driver.value?.licenseSeatRegistrations?.some(
    (reg) => reg.endDate === null
  );

  return hasActiveRegistration ? model.value?.licenseSeat : undefined;
});

const reset = () => {
  driver.value = getDriverCopy(props.modelValue);
};

const campaignList = (query: string, select: any) => {
  const input = query || select.modelValue?.split('/')?.slice(-1)?.[0];
  if (!input) return Promise.resolve([]);
  const filterQuery: ProviderParams = {
    filter: query ? { 'group.name': input } : { id: input },
    sort: {
      sortKey: 'group.name',
      sortDirection: 'ASC',
    },
  };
  return collOptionsProvider<Campaign>('/campaigns', filterQuery, (value) => ({
    label: value.name,
    value: value['@id'],
  }))();
};

const boxBaseSurfaceOptions = enumOptionsProvider('/enums/BoxBaseSurface', (item) => ({
  value: item,
  label: t(`box.boxBaseSurface.${item}`),
}));

const addBox = () => {
  driver.value.box = {
    firstCampaign: undefined,
    mountingDate: undefined,
    status: undefined,
    type: undefined,
    sealNumber: undefined,
    alreadyPayed: 0,
    totalPrice: undefined,
    depot: undefined,
  } as unknown as typeof driver.value.box;
};

const removeBox = () => {
  if (props.modelValue?.box) {
    boxModalOpen.value = true;
  } else {
    driver.value.box = undefined;
  }
};

const removeFromLicenseSeat = () => {
  if (!driver.value?.licenseSeatRegistrations) {
    notify?.({
      variant: 'error',
      title: t('pages.driver.details.licenseSeatCouldNotBeRemoved'),
    });
    return;
  }

  const licenseSeatRegistration = findLatestLicenseSeat(driver.value.licenseSeatRegistrations);
  if (licenseSeatRegistration) {
    licenseSeatRegistration.endDate = new Date();
  } else {
    notify?.({
      variant: 'error',
      title: t('pages.driver.details.licenseSeatCouldNotBeRemoved'),
    });
  }
};

const fetchLanguages = enumOptionsProvider('/enums/Language', (value) => ({
  value,
  label: t(`language.${value}`),
}));

const fetchDriverNoticeComment = enumOptionsProvider('/enums/DriverNoticeComment', (value) => ({
  value,
  label: t(`driver.noticeComment.${value}`),
}));

const fetchRegions = collOptionsProvider<Region>(
  '/regions',
  {
    sort: {
      sortKey: 'name',
      sortDirection: 'ASC',
    },
  },
  (value) => ({
    label: value.name,
    value: value['@id'],
  }),
);

const notify = useNotify();

const {
  data: validatedDriver,
  errors,
  validatedFn,
  setValidation,
} = useValidation(
  object({
    iban: string()
      .nullable()
      .transform((value) => value?.replace(/\s/g, '') ?? '')
      .matches(/^CH\d{7}[0-9A-Z]{12}$/, {
        excludeEmptyString: true,
        message: t('validation.iban'),
      }),
    bankName: string().nullable(),
    payee: string().nullable(),
    region: string().required(t('validation.notEmpty')),
    specialDriverPhoto: boolean().required(t('validation.notEmpty')),
    specialDriverStanding: boolean().required(t('validation.notEmpty')),
    helperDriver: boolean().required(t('validation.notEmpty')),
    bicycleType: string().nullable(),
    noticeComment: string().nullable(),
    caredForAt: date().nullable(),
    defaultSourceAddress: lazy((value) =>
      typeof value === 'object'
        ? object({
            street: string().nullable(),
            zip: string()
              .matches(/^[1-9]\d{3}$/, {
                excludeEmptyString: true,
                message: t('validation.plz'),
              })
              .nullable(),
            cityName: string().nullable(),
          }).nullable()
        : mixed(),
    ),
    defaultDestinationAddress: lazy((value) =>
      typeof value === 'object'
        ? object({
            street: string().nullable(),
            zip: string()
              .matches(/^[1-9]\d{3}$/, {
                excludeEmptyString: true,
                message: t('validation.plz'),
              })
              .nullable(),
            cityName: string().nullable(),
          }).nullable()
        : mixed(),
    ),
    box: lazy((value) =>
      typeof value === 'object'
        ? object({
            firstCampaign: string().nullable(),
            mountingDate: date().nullable().required(t('validation.notEmpty')),
            status: string().nullable().required(t('validation.notEmpty')),
            type: string().nullable().required(t('validation.notEmpty')),
            sealNumber: string().nullable(),
            alreadyPayed: number().nullable().typeError(t('validation.notEmpty')).required(t('validation.notEmpty')),
            totalPrice: number().nullable(),
            depot: string().nullable(),
            stable: string().nullable().required(t('validation.notEmpty')),
            keyNumber: string(),
          }).nullable()
        : mixed(),
    ),
    warningLevel: string().nullable().required(t('validation.notEmpty')),
    outstandingDepot: number().nullable().typeError(t('validation.notEmpty')).required(t('validation.notEmpty')),
    profileComplete: boolean().nullable().required(t('validation.notEmpty')),
    liabilityDisclaimerSigned: boolean().nullable().required(t('validation.notEmpty')),
    requestedLoyaltyPointReward: boolean().nullable().required(t('validation.notEmpty')),
    estToursPerWeek: number()
      .nullable()
      .transform((item, originalValue) => (originalValue === '' ? null : item)),
    estAvgKmPerDay: number()
      .nullable()
      .transform((item, originalValue) => (originalValue === '' ? null : item)),
    user: object({
      salutation: string().required(t('validation.notEmpty')),
      firstName: string().required(t('validation.notEmpty')),
      lastName: string().required(t('validation.notEmpty')),
      email: string().email(t('validation.emailValid')).required(t('validation.notEmpty')),
      phoneNumber: string()
        .matches(/^\+[1-9]\d{1,14}$/, {
          excludeEmptyString: true,
          message: t('validation.phoneValid'),
        })
        .transform((value, oldValue) => {
          let newVal = '';

          newVal = value?.replace(/\s/g, '') ?? '';
          if (newVal.startsWith('00')) {
            newVal = `+${newVal.substring(2)}`;
          }
          return newVal;
        })
        .nullable()
        .required(t('validation.notEmpty')),
      language: string().required(t('validation.notEmpty')),
      active: boolean().required(t('validation.notEmpty')),
      birthday: date().nullable(),
      address: object({
        street: string().nullable(),
        zip: string()
          .matches(/^[1-9]\d{3}$/, {
            excludeEmptyString: true,
            message: t('validation.plz'),
          })
          .nullable(),
        cityName: string().nullable(),
      }).nullable(),
    }).required(t('validation.notEmpty')),
    companyProfileComplete: boolean().required(t('validation.notEmpty')),
    anonymousRanking: boolean().nullable(),
    canton: string().nullable(),
    luggageRackUsable: boolean().nullable(),
    saddlebags: boolean().nullable(),
    luggageRackInBack: boolean().nullable(),
    batteryPosition: string().nullable(),
    licenseSeat: object().nullable(),
  }),
  driver,
);

const save = validatedFn(async () => {
  try {
    const { user } = validatedDriver.value;
    const newDriver = await exec<Driver>(`/drivers/${validatedDriver.value.id}`, {
      method: 'PUT',
      body: {
        iban: validatedDriver.value.iban,
        payee: validatedDriver.value.payee,
        bankName: validatedDriver.value.bankName,
        region: validatedDriver.value.region,
        specialDriverPhoto: validatedDriver.value.specialDriverPhoto,
        specialDriverStanding: validatedDriver.value.specialDriverStanding,
        helperDriver: validatedDriver.value.helperDriver,
        bicycleType: validatedDriver.value.bicycleType,
        noticeComment: validatedDriver.value.noticeComment,
        caredForAt: validatedDriver.value.caredForAt,
        defaultSourceAddress: {
          ...validatedDriver.value.defaultSourceAddress,
          id: validatedDriver.value.defaultSourceAddress['@id'],
        },
        defaultDestinationAddress: {
          ...validatedDriver.value.defaultDestinationAddress,
          id: validatedDriver.value.defaultDestinationAddress['@id'],
        },
        box: validatedDriver.value?.box
          ? {
              ...validatedDriver.value?.box,
              sealNumber: validatedDriver.value?.box.sealNumber || null,
              id: validatedDriver.value?.box?.['@id']!,
              stable: validatedDriver.value?.box.stable === 'true',
            }
          : null,
        warningLevel: validatedDriver.value?.warningLevel,
        outstandingDepot: validatedDriver.value?.outstandingDepot,
        profileComplete: validatedDriver.value?.profileComplete,
        liabilityDisclaimerSigned: validatedDriver.value?.liabilityDisclaimerSigned,
        requestedLoyaltyPointReward: validatedDriver.value?.requestedLoyaltyPointReward,
        estToursPerWeek: validatedDriver.value?.estToursPerWeek,
        estAvgKmPerDay: validatedDriver.value?.estAvgKmPerDay,
        verificationRequested: validatedDriver.value?.verificationRequested,
        companyProfileComplete: validatedDriver.value?.companyProfileComplete,
        anonymousRanking: validatedDriver.value?.anonymousRanking,
        canton: validatedDriver.value?.canton,
        luggageRackUsable: validatedDriver.value?.luggageRackUsable,
        saddlebags: validatedDriver.value?.saddlebags,
        luggageRackInBack: validatedDriver.value?.luggageRackInBack,
        batteryPosition: validatedDriver.value?.batteryPosition,
        ...(validatedDriver.value?.licenseSeat ? {} : { licenseSeat: null }),
      },
    });

    newDriver.user = await exec<User>(`/users/${user.id}`, {
      method: 'PUT',
      body: {
        id: user['@id'],
        salutation: user.salutation,
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        language: user.language,
        birthday: user.birthday,
        address: {
          ...user.address,
          id: user.address['@id'],
        },
        active: user.active,
        phoneNumber: user.phoneNumber,
      },
    });

    if (validatedDriver.value?.licenseSeatRegistrations) {
      await Promise.all(validatedDriver.value.licenseSeatRegistrations.map((registration) => {
        return exec<LicenseSeatRegistration>(`/license_seat_registrations/${registration.id}`, {
          method: 'PUT',
          body: {
            ...registration,
            licenseSeat: registration.licenseSeat?.['@id'],
          },
        });
      }));
    }

    notify?.({
      variant: 'success',
      title: t('pages.driver.details.saveSuccess'),
    });

    emit('reload');
    emit('update:modelValue', newDriver);
  } catch (e) {
    console.error(e);
    notify?.({
      variant: 'error',
      title: t('saveError'),
    });
  }
});

const licenseTypeText = computed(() => {
  return licenseSeat.value?.type === LicenseType.NO_BOX ? t('driver.licenseTypeNoBox') : t('driver.licenseTypeBox');
});
</script>

<style lang="scss" scoped>
form {
  --card-padding: #{px(60)};
}
.card-section {
  @apply grid grid-cols-2 gap-x-5 gap-y-7.5 w-full my-7.5;
}
.form-group {
  @apply flex flex-col gap-y-7.5;
  @apply px-7.5 py-4;
  @apply rounded-4 border border-table-head-gray;
  @apply -mx-7.5;
}

.trash {
  @apply bg-red-100;
  @apply h-10 w-10;
  @apply p-2.5;
  @apply inline-block;
  @apply ml-2;
  color: white;
  border-radius: 100%;
}

.add-icon {
  @apply h-12 w-12;
  @apply p-2.5;
  @apply inline-block;
  border-radius: 100%;
}
</style>
